import React, { useEffect, useState } from "react";
import { Prompt } from "react-router-dom";
import { InputField } from "components/designV2/inputfield/inputfield";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { Text } from "components/designV2/text/text";
import { ColorV2 } from "config/colors";
import { useTranslation } from "react-i18next";
import { useAlertStore } from "stores/alertStore/alertStore";
import { LocationMap } from "web-apps/company/components/location/locationMap.component";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  DetailsActions,
  DetailsFieldSlot,
  DetailsForm,
  DetailsPageTitle,
  DetailsReadOnlyField,
  DetailsReadOnlyLabel,
  DetailsReadOnlyValue,
  DetailsRow,
  DetailsSection,
  DetailsSectionTitle,
} from "./detailsSettings.styled";

type Props = {
  view?: string;
};

export const DetailsSettings: React.FC<Props> = ({ view }) => {
  const { t } = useTranslation();
  const [, , alertDispatch] = useAlertStore();
  const [legalName, setLegalName] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [location, setLocation] = useState("");
  const [companyState, companyActions] = useCompanyStore();

  useEffect(() => {
    if (!companyState.company) {
      return;
    }

    setLegalName(companyState.company.legalName || "");
    setRegistrationNumber(companyState.company.registrationNumber || "");
    setPhoneNumber(companyState.company.phoneNumber || "");
    setAddress(companyState.company.address || "");
    setPostalCode(companyState.company.postalCode || "");
    setCity(companyState.company.city || "");
    setLocation(companyState.company.location || "");
  }, [companyState.company]);

  const validate = (): boolean => {
    return Boolean(
      legalName && address && location && city && phoneNumber && postalCode,
    );
  };

  const validateAddress = (): boolean => {
    return Boolean(city && address && postalCode && location);
  };

  const madeChanges = (): boolean => {
    const company = companyState.company;
    if (!company) return false;

    return (
      legalName !== (company.legalName || "") ||
      address !== company.address ||
      location !== company.location ||
      city !== company.city ||
      phoneNumber !== company.phoneNumber ||
      postalCode !== company.postalCode
    );
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (!validate() || !companyState.company) {
      return;
    }

    const data = {
      ...companyState.company,
      legalName,
      phoneNumber,
      address,
      postalCode,
      city,
      location,
    };

    companyActions
      .saveCompany(data, companyState.companies, companyState.company.id)
      .then(() => {
        alertDispatch({
          type: "SHOW_ALERT",
          payload: {
            icon: "checkmark",
            title: t("Alerts.Updated"),
            message: t("ProfileCompany.CompanyHasBeenUpdated"),
          },
        });
      })
      .catch(() => {
        alertDispatch({
          type: "SHOW_ALERT",
          payload: {
            icon: "alert",
            title: t("Alerts.OhNo"),
            message: t("ProfileCompany.CompanyCouldNotBeUpdated"),
          },
        });
      });
  };

  const saveDisabled = !validate() || !madeChanges() || !validateAddress();

  return (
    <>
      <Prompt
        when={madeChanges()}
        message={t("ProfileCompany.YouHaveUnSavedChanges")}
      />
      <DetailsForm onSubmit={handleSubmit}>
        <DetailsPageTitle>{t("ProfileCompany.EditDetails")}</DetailsPageTitle>

        <DetailsSection>
          <DetailsSectionTitle>
            {t("ProfileCompany.CompanyDetails")}
          </DetailsSectionTitle>
          <DetailsRow $view={view}>
            <DetailsFieldSlot>
              <InputField
                label={t("ProfileCompany.LegalName")}
                value={legalName}
                onChange={setLegalName}
                onClear={() => setLegalName("")}
                fullWidth
              />
            </DetailsFieldSlot>
          </DetailsRow>

          <DetailsRow $view={view}>
            <DetailsFieldSlot>
              <DetailsReadOnlyField>
                <DetailsReadOnlyLabel typography="b2" color={ColorV2.Blue4}>
                  {t("ProfileCompany.RegistrationNumber")}
                </DetailsReadOnlyLabel>
                <DetailsReadOnlyValue typography="b2" color={ColorV2.Blue4}>
                  {registrationNumber}
                </DetailsReadOnlyValue>
              </DetailsReadOnlyField>
            </DetailsFieldSlot>
            {companyState.company?.referenceCode && (
              <DetailsFieldSlot>
                <DetailsReadOnlyField>
                  <DetailsReadOnlyLabel typography="b2" color={ColorV2.Blue4}>
                    {t("ProfileCompany.CompanyCode")}
                  </DetailsReadOnlyLabel>
                  <DetailsReadOnlyValue typography="b2" color={ColorV2.Blue4}>
                    {companyState.company.referenceCode}
                  </DetailsReadOnlyValue>
                </DetailsReadOnlyField>
              </DetailsFieldSlot>
            )}
          </DetailsRow>

          <DetailsRow $view={view}>
            <DetailsFieldSlot>
              <InputField
                label={t("ProfileCompany.PhoneNumber")}
                value={phoneNumber}
                onChange={setPhoneNumber}
                onClear={() => setPhoneNumber("")}
                fullWidth
              />
            </DetailsFieldSlot>
            <DetailsFieldSlot>
              <DetailsReadOnlyField>
                <DetailsReadOnlyLabel typography="b2" color={ColorV2.Blue4}>
                  {t("ProfileCompany.Email")}
                </DetailsReadOnlyLabel>
                <DetailsReadOnlyValue typography="b2" color={ColorV2.Blue4}>
                  {companyState.company?.email || ""}
                </DetailsReadOnlyValue>
              </DetailsReadOnlyField>
            </DetailsFieldSlot>
          </DetailsRow>
        </DetailsSection>

        <DetailsSection>
          <DetailsSectionTitle>
            {t("ProfileCompany.CompanyAddress")}
          </DetailsSectionTitle>
          <DetailsRow $view={view}>
            <DetailsFieldSlot>
              <Text typography="b2" color={ColorV2.Blue4}>
                {t("ProfileCompany.Address")}
              </Text>
              <LocationMap
                address={address && city ? `${address}, ${city}` : ""}
                setAddress={setAddress}
                setCity={setCity}
                setPostalCode={setPostalCode}
                setLocation={setLocation}
                inRegistration={true}
                noMap
                hasIncompleteAddress={!validateAddress() && address !== ""}
              />
            </DetailsFieldSlot>
          </DetailsRow>
        </DetailsSection>

        <DetailsActions>
          <PrimaryButton
            type="submit"
            label={t("General.Save")}
            disabled={saveDisabled}
          />
        </DetailsActions>
      </DetailsForm>
    </>
  );
};
